import type {
  CreateReportRequestDto,
  CreateReportTemplateRequestDto,
  CreateTemplateFromReportRequestDto,
  ReportCustomerDto,
  ReportDetailDto,
  ReportDto,
  RefreshReportSnapshotsResponseDto,
  ReportMeasurementPeriodOptionDto,
  ReportReportingMonthOptionDto,
  ReportSectionDto,
  ReportTemplateDto,
  ReorderReportSectionsRequestDto,
  SectionLibraryResponseDto,
  UpdateReportCustomerSettingsDto,
  UpdateReportRequestDto,
  UpdateReportSectionRequestDto,
  UpdateReportTemplateRequestDto,
} from '@act/shared/data-transfer-objects';
import { EndpointDefinition } from '../utils/endpointDefinition';

const REPORTS_PREFIX = '/reports';

/** API endpoint definitions for the reports subdomain backend. */
export const reportsRequests = {
  listCustomers: EndpointDefinition.get<
    ReportCustomerDto[],
    { syncFromWarehouse?: string }
  >({
    name: 'reportsListCustomers',
    route: `${REPORTS_PREFIX}/customers`,
  }),

  getCustomer: EndpointDefinition.get<ReportCustomerDto, { id: string }>({
    name: 'reportsGetCustomer',
    route: `${REPORTS_PREFIX}/customers/:id`,
  }),

  listCustomerMeasurementPeriods: EndpointDefinition.get<
    ReportMeasurementPeriodOptionDto[],
    { id: string }
  >({
    name: 'reportsListCustomerMeasurementPeriods',
    route: `${REPORTS_PREFIX}/customers/:id/measurement-periods`,
  }),

  listCustomerReportingMonths: EndpointDefinition.get<
    ReportReportingMonthOptionDto[],
    { id: string }
  >({
    name: 'reportsListCustomerReportingMonths',
    route: `${REPORTS_PREFIX}/customers/:id/reporting-months`,
  }),

  updateCustomerSettings: EndpointDefinition.patch<
    ReportCustomerDto,
    UpdateReportCustomerSettingsDto,
    { id: string }
  >({
    name: 'reportsUpdateCustomerSettings',
    route: `${REPORTS_PREFIX}/customers/:id`,
  }),

  getSectionLibrary: EndpointDefinition.get<SectionLibraryResponseDto, null>({
    name: 'reportsGetSectionLibrary',
    route: `${REPORTS_PREFIX}/section-library`,
  }),

  listReports: EndpointDefinition.get<
    ReportDto[],
    { customerId?: string }
  >({
    name: 'reportsListReports',
    route: `${REPORTS_PREFIX}/reports`,
  }),

  createReport: EndpointDefinition.post<
    ReportDetailDto,
    CreateReportRequestDto,
    null
  >({
    name: 'reportsCreateReport',
    route: `${REPORTS_PREFIX}/reports`,
  }),

  getReport: EndpointDefinition.get<ReportDetailDto, { id: string }>({
    name: 'reportsGetReport',
    route: `${REPORTS_PREFIX}/reports/:id`,
  }),

  getLiveSectionSnapshot: EndpointDefinition.get<
    { dataSnapshot: Record<string, unknown> | null },
    { id: string; sectionId: string }
  >({
    name: 'reportsGetLiveSectionSnapshot',
    route: `${REPORTS_PREFIX}/reports/:id/live-snapshots/:sectionId`,
  }),

  updateReport: EndpointDefinition.patch<
    ReportDetailDto,
    UpdateReportRequestDto,
    { id: string }
  >({
    name: 'reportsUpdateReport',
    route: `${REPORTS_PREFIX}/reports/:id`,
  }),

  refreshReportSnapshots: EndpointDefinition.post<
    RefreshReportSnapshotsResponseDto,
    null,
    { id: string }
  >({
    name: 'reportsRefreshReportSnapshots',
    route: `${REPORTS_PREFIX}/reports/:id/refresh-snapshots`,
  }),

  archiveReport: EndpointDefinition.post<ReportDetailDto, null, { id: string }>({
    name: 'reportsArchiveReport',
    route: `${REPORTS_PREFIX}/reports/:id/archive`,
  }),

  unarchiveReport: EndpointDefinition.post<
    ReportDetailDto,
    null,
    { id: string }
  >({
    name: 'reportsUnarchiveReport',
    route: `${REPORTS_PREFIX}/reports/:id/unarchive`,
  }),

  updateReportSection: EndpointDefinition.patch<
    ReportSectionDto,
    UpdateReportSectionRequestDto,
    { id: string; sectionRowId: string }
  >({
    name: 'reportsUpdateReportSection',
    route: `${REPORTS_PREFIX}/reports/:id/sections/:sectionRowId`,
  }),

  reorderReportSections: new EndpointDefinition<
    ReportDetailDto,
    ReorderReportSectionsRequestDto,
    { id: string }
  >({
    name: 'reportsReorderReportSections',
    route: `${REPORTS_PREFIX}/reports/:id/sections/reorder`,
    method: 'PUT',
  }),

  listTemplates: EndpointDefinition.get<
    ReportTemplateDto[],
    { customerId?: string }
  >({
    name: 'reportsListTemplates',
    route: `${REPORTS_PREFIX}/templates`,
  }),

  createTemplate: EndpointDefinition.post<
    ReportTemplateDto,
    CreateReportTemplateRequestDto,
    null
  >({
    name: 'reportsCreateTemplate',
    route: `${REPORTS_PREFIX}/templates`,
  }),

  createTemplateFromReport: EndpointDefinition.post<
    ReportTemplateDto,
    CreateTemplateFromReportRequestDto,
    { reportId: string }
  >({
    name: 'reportsCreateTemplateFromReport',
    route: `${REPORTS_PREFIX}/templates/from-report/:reportId`,
  }),

  updateTemplate: EndpointDefinition.patch<
    ReportTemplateDto,
    UpdateReportTemplateRequestDto,
    { id: string }
  >({
    name: 'reportsUpdateTemplate',
    route: `${REPORTS_PREFIX}/templates/:id`,
  }),

  deleteTemplate: EndpointDefinition.delete<void, { id: string }>({
    name: 'reportsDeleteTemplate',
    route: `${REPORTS_PREFIX}/templates/:id`,
  }),
};
